// apps/web/src/lib/permissions.ts

import type { WarehouseRole } from '@/types';
import { isLegacyRedirect } from './legacy-redirects';

// Re-export for convenience so consumers can import from one place
export type { WarehouseRole } from '@/types';

// ============================================
// ROUTE ACCESS CONFIGURATION
// ============================================

// Special access levels (not warehouse roles)
type SpecialAccess = 'ADMIN_ONLY' | 'ANY_AUTHENTICATED';

export interface RoutePermission {
  path: string;
  // Roles that can access this route. Empty array = ADMIN_ONLY.
  // 'ANY_AUTHENTICATED' means any logged-in user can access.
  allowedRoles: WarehouseRole[] | SpecialAccess;
}

// Manager roles that imply operator access (mirrors backend ROLE_HIERARCHY)
export const ROLE_HIERARCHY: Record<string, WarehouseRole[]> = {
  WAREHOUSE_MANAGER: [
    'PICKER',
    'PACKER',
    'RECEIVER',
    'PUTAWAY_OPERATOR',
    'LOADER',
    'MANUFACTURER',
    'RECOUNTER',
    'WRAPPER',
    'COUNTER',
    'COUNT_MANAGER',
  ],
  COUNT_MANAGER: ['COUNTER'],
  PICKING_MANAGER: ['PICKER', 'RECOUNTER', 'WRAPPER'],
  PACKING_MANAGER: ['PACKER'],
  RECEIVING_MANAGER: ['RECEIVER'],
  MANUFACTURING_MANAGER: ['MANUFACTURER'],
};

export const ROUTE_PERMISSIONS: RoutePermission[] = [
  // === DASHBOARD ===
  { path: '/', allowedRoles: 'ANY_AUTHENTICATED' },

  // === REFERENCE DATA ===
  // Backend: WarehousesController -- guarded
  { path: '/warehouses', allowedRoles: ['WAREHOUSE_MANAGER'] },
  { path: '/warehouses/:id', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Backend: ItemsController -- guarded
  {
    path: '/items',
    allowedRoles: ['WAREHOUSE_MANAGER', 'MANUFACTURING_MANAGER'],
  },
  {
    path: '/items/:id',
    allowedRoles: ['WAREHOUSE_MANAGER', 'MANUFACTURING_MANAGER'],
  },
  // Backend: UomController -- guarded
  { path: '/uom', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Backend: VendorsController -- guarded
  {
    path: '/vendors',
    allowedRoles: ['RECEIVING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  {
    path: '/vendors/:id',
    allowedRoles: ['RECEIVING_MANAGER', 'WAREHOUSE_MANAGER'],
  },

  // === INVENTORY ===
  // Backend: InventoryController -- some GETs auth-only (Category B fix will add guards)
  {
    path: '/inventory',
    allowedRoles: ['WAREHOUSE_MANAGER', 'RECEIVER', 'PUTAWAY_OPERATOR'],
  },
  // Backend: KitchenPlanningController (kitchen module K-8). Read-only
  // dashboard for every kitchen role (plan §12, KITCHEN_PLANNING_ROLES).
  {
    path: '/kitchens/planning',
    allowedRoles: [
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  // Backend: KitchenLabelsController — Legacy Labels: the old kitchen label
  // upload + printing (interim, K-18; new projects go to /kitchens/projects).
  // WAREHOUSE_MANAGER listed explicitly (not via ROLE_HIERARCHY), the same
  // way /kit-assembly does it.
  {
    path: '/kitchen-labels',
    allowedRoles: ['MULTI_FAMILY', 'WAREHOUSE_MANAGER'],
  },
  {
    path: '/kitchen-labels/:id',
    allowedRoles: ['MULTI_FAMILY', 'WAREHOUSE_MANAGER'],
  },
  // Backend: KitchenCatalogController — the kitchen product catalog (kitchen
  // module K-3). Read by the four kitchen roles; writes are API-gated to
  // KITCHEN_ENGINEER + WAREHOUSE_MANAGER and hidden in the UI otherwise.
  // Roles listed explicitly (no ROLE_HIERARCHY entry), as /kitchen-labels.
  {
    path: '/kitchens/catalog',
    allowedRoles: [
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  {
    path: '/kitchens/catalog/cabinets/:id',
    allowedRoles: [
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  // Backend: KitchenProjectsController (kitchen module K-6). Read by every
  // kitchen role (plan §12); writes are API-gated per handler.
  {
    path: '/kitchens/projects',
    allowedRoles: [
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  {
    path: '/kitchens/projects/:id',
    allowedRoles: [
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  // Kitchen engineering review (K-9). Backend: KitchenEngineeringController —
  // KITCHEN_ENGINEER + WAREHOUSE_MANAGER act, MULTI_FAMILY +
  // MANUFACTURING_MANAGER read (the page hides the write buttons).
  {
    path: '/kitchens/engineering',
    allowedRoles: [
      'KITCHEN_ENGINEER',
      'WAREHOUSE_MANAGER',
      'MULTI_FAMILY',
      'MANUFACTURING_MANAGER',
    ],
  },
  {
    path: '/kitchens/engineering/:id',
    allowedRoles: [
      'KITCHEN_ENGINEER',
      'WAREHOUSE_MANAGER',
      'MULTI_FAMILY',
      'MANUFACTURING_MANAGER',
    ],
  },
  // Mike Zabloski's engineering names + spec status (kitchens end to end
  // PR-E2). Backend: KitchenEngineeringNamesController — every kitchen catalog
  // reader opens it read-only; writes need the per-user grant
  // `kitchen-engineering-names:edit` (user_permission_grants) or admin.
  {
    path: '/kitchens/engineering-names',
    allowedRoles: [
      'KITCHEN_ENGINEER',
      'WAREHOUSE_MANAGER',
      'MULTI_FAMILY',
      'MANUFACTURING_MANAGER',
    ],
  },
  // Kitchens → Acumatica (PR-A2, kitchens end to end §3.2). Backend:
  // KitchenErpBridgeController — every kitchen role reads the overview
  // ("Try again" is API-gated to the kitchen-order roles); the colours page
  // is purchasing's (route permission `kitchens-acumatica-colours`): the
  // receiving / purchase-order managers + warehouse managers, assignable to
  // other roles on the Roles page. Admins bypass both.
  {
    path: '/kitchens/acumatica',
    allowedRoles: [
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  {
    path: '/kitchens/acumatica/colours',
    allowedRoles: ['RECEIVING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  // Kitchens → Production (KM-5 / KM-6, plan kitchens-in-closet-manufacturing
  // §6.7): the five production pages carry the roles the retired
  // /kitchens/work-orders pages had — the API checks tier R / S / L / M per
  // action (403 KITCHEN_LINE_FORBIDDEN). Mirrored in known-routes.ts,
  // seed-roles-data.ts and the 20261022213000 seed migration; the
  // /manufacturing-* pages are unchanged (and never show kitchen data).
  {
    path: '/kitchens/production',
    allowedRoles: [
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
    ],
  },
  {
    path: '/kitchens/production/work-orders',
    allowedRoles: [
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
    ],
  },
  {
    path: '/kitchens/production/work-orders/:id',
    allowedRoles: [
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
    ],
  },
  {
    path: '/kitchens/production/board',
    allowedRoles: [
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
    ],
  },
  {
    path: '/kitchens/production/scan',
    allowedRoles: [
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
    ],
  },
  // Kitchens → Production → Count parts (2026-11-03, plan
  // kitchens-production-shipping P-6): the kitchen end-of-line count — the
  // kitchen tier S of the /manufacturing-work-orders/kitchen/end-count routes.
  // Mirrored in known-routes.ts, seed-roles-data.ts and the 20261103150100 seed migration.
  {
    path: '/kitchens/production/count',
    allowedRoles: [
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  // Kitchens → Assembly (kitchens end to end PR-L1, plan §7.1): the assembly
  // station — finish → label 1 → scan → assembled. The production roles; the
  // API tiers each action (L finish / reprint / scan / undo, M batch print).
  {
    path: '/kitchens/assembly',
    allowedRoles: [
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MULTI_FAMILY',
      'KITCHEN_ENGINEER',
    ],
  },
  // Kitchens → Shipping (kitchens end to end PR-S1, plan §8.1–§8.2): the
  // loading dock — label 2, start a truck, scan each cabinet, "Ship this
  // truck now" / "Finish this order short". The dock roles; the API tiers
  // each action (S scan / open / undo / print, M close — default 16).
  {
    path: '/kitchens/shipping',
    allowedRoles: [
      'LOADER',
      'MULTI_FAMILY',
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  {
    path: '/kitchens/shipping/loads/:loadId',
    allowedRoles: [
      'LOADER',
      'MULTI_FAMILY',
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  // Backend: CountBatchesController / CountingController (cycle counting v2)
  { path: '/count-batches', allowedRoles: ['COUNT_MANAGER'] },
  { path: '/count-batches/:id', allowedRoles: ['COUNT_MANAGER'] },
  { path: '/counting', allowedRoles: ['COUNTER'] },
  // MUST stay above /counting/:taskId — canAccessRoute resolves with `.find`
  // and matchPath treats `:taskId` as a wildcard, so the dynamic entry
  // already matches '/counting/aisle'. Below it this line is dead code.
  { path: '/counting/aisle', allowedRoles: ['COUNTER'] },
  { path: '/counting/:taskId', allowedRoles: ['COUNTER'] },
  // Backend: AdjustmentsController -- guarded @WarehouseRoles('INVENTORY_MANAGER').
  // Inventory managers may CREATE only RELOCATION (moves) — the service
  // enforces WAREHOUSE_MANAGER for every other adjustment type.
  {
    path: '/adjustments',
    allowedRoles: ['WAREHOUSE_MANAGER', 'INVENTORY_MANAGER'],
  },
  {
    path: '/adjustments/:id',
    allowedRoles: ['WAREHOUSE_MANAGER', 'INVENTORY_MANAGER'],
  },
  // Backend: BulkInventoryReceiveController -- @WarehouseRoles('INVENTORY_MANAGER')
  // Manager-tier batch surface; line-operator receiving stays on PO Receive.
  { path: '/inventory/bulk-receive', allowedRoles: ['INVENTORY_MANAGER'] },
  // Backend: BulkInventoryMoveController -- @WarehouseRoles('INVENTORY_MANAGER')
  // Intra-warehouse bin-to-bin batch relocation; same manager-tier gate.
  { path: '/inventory/bulk-move', allowedRoles: ['INVENTORY_MANAGER'] },
  // Backend: BulkInventoryAdjustmentController + BulkRollbackController
  // Unified Bulk Imports history + rollback UX. Same role gate as the
  // per-feature surfaces above; the rollback endpoint also enforces
  // INVENTORY_MANAGER server-side.
  { path: '/inventory/bulk-imports', allowedRoles: ['INVENTORY_MANAGER'] },
  { path: '/inventory/bulk-imports/:id', allowedRoles: ['INVENTORY_MANAGER'] },
  // Inventory ledger + per-SKU audit detail — same audience as the on-hand list
  // (/inventory). NOTE: /inventory/:itemId is a single-segment dynamic route, so it
  // MUST stay AFTER every static /inventory/* entry above (first-match-wins matcher).
  {
    path: '/inventory/ledger',
    allowedRoles: ['WAREHOUSE_MANAGER', 'RECEIVER', 'PUTAWAY_OPERATOR'],
  },
  {
    path: '/inventory/:itemId',
    allowedRoles: ['WAREHOUSE_MANAGER', 'RECEIVER', 'PUTAWAY_OPERATOR'],
  },
  // Backend: TransferOrdersController -- guarded
  { path: '/transfer-orders', allowedRoles: ['WAREHOUSE_MANAGER'] },
  { path: '/transfer-orders/:id', allowedRoles: ['WAREHOUSE_MANAGER'] },

  // === WORKFORCE ===
  {
    path: '/workforce',
    allowedRoles: ['WAREHOUSE_MANAGER', 'PICKING_MANAGER'],
  },

  // === INBOUND ===
  // Backend: PurchaseOrdersController -- @WarehouseRoles('RECEIVING_MANAGER', 'RECEIVER')
  { path: '/purchase-orders', allowedRoles: ['RECEIVING_MANAGER', 'RECEIVER'] },
  {
    path: '/purchase-orders/:id',
    allowedRoles: ['RECEIVING_MANAGER', 'RECEIVER'],
  },
  // Backend: PutawayController -- guarded WAREHOUSE_MANAGER, PUTAWAY_OPERATOR
  { path: '/putaway', allowedRoles: ['WAREHOUSE_MANAGER', 'PUTAWAY_OPERATOR'] },

  // === OUTBOUND ===
  // Backend: PickingController -- @WarehouseRoles('PICKING_MANAGER', 'PICKER') for pick execution
  { path: '/picking', allowedRoles: ['PICKING_MANAGER', 'PICKER'] },
  { path: '/picking/:id', allowedRoles: ['PICKING_MANAGER', 'PICKER'] },
  // Backend: Short picks -- the list/orders reads and the resolve, resolve-all
  // and escalate-all writes are @WarehouseRoles('PICKING_MANAGER', 'RECOUNTER').
  // Recounters own short-pick triage on the floor, so they get the page too.
  { path: '/short-picks', allowedRoles: ['PICKING_MANAGER', 'RECOUNTER'] },
  // Backend: GatherListsController gather routes --
  // @WarehouseRoles('PICKING_MANAGER', 'WAREHOUSE_MANAGER', 'PICKER').
  // A gatherer is a floor worker, so PICKER is the accurate role; managers
  // reach it through ROLE_HIERARCHY. STATIC before DYNAMIC: canAccessRoute
  // resolves with `.find` and matchPath treats `:listId` as a wildcard.
  {
    path: '/gather',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER', 'PICKER'],
  },
  {
    path: '/gather/:listId',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER', 'PICKER'],
  },
  // Backend: SalesOrdersController -- guarded
  {
    path: '/orders-dashboard',
    allowedRoles: ['PICKING_MANAGER', 'PACKING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  {
    path: '/sales-orders',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  {
    path: '/sales-orders/:id',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER'],
  },

  // === RECOUNT ===
  // Backend: RecountController -- guarded. PICKER included: pickers run
  // recounts in this warehouse (RECOUNTER is the dedicated role; managers
  // get it via hierarchy).
  {
    path: '/recount',
    allowedRoles: ['PICKING_MANAGER', 'PICKER', 'RECOUNTER'],
  },
  {
    path: '/recount/:id',
    allowedRoles: ['PICKING_MANAGER', 'PICKER', 'RECOUNTER'],
  },

  // === WRAPPING ===
  // Backend: WrappingController -- guarded. WRAPPER is the dedicated role;
  // PICKING_MANAGER + WAREHOUSE_MANAGER get it via ROLE_HIERARCHY.
  { path: '/wrapping', allowedRoles: ['PICKING_MANAGER', 'WRAPPER'] },
  { path: '/wrapping/:id', allowedRoles: ['PICKING_MANAGER', 'WRAPPER'] },

  // === MANUFACTURING ===
  // Backend: ManufacturingDashboardController, WorkOrdersController, KitAssemblyController
  {
    path: '/manufacturing-dashboard',
    allowedRoles: [
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MANUFACTURER',
    ],
  },
  // mfg-program PR-A: par + percent-of-par thresholds. Read by everyone who
  // reads the dashboard; writes are enforced by PUT
  // /manufacturing-dashboard/settings (P7: global = admins, overrides =
  // MANUFACTURING_MANAGER / WAREHOUSE_MANAGER at that warehouse).
  {
    path: '/settings/manufacturing',
    allowedRoles: [
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MANUFACTURER',
    ],
  },
  // Must equal the backend @WarehouseRoles set on every sku-needs route.
  {
    path: '/sku-needs',
    allowedRoles: ['WAREHOUSE_MANAGER', 'MANUFACTURING_MANAGER'],
  },
  // Ship Schedule tab (Acumatica's forward shipment book) — same guard as the
  // rest of the sku-needs controller.
  {
    path: '/sku-needs/ship-schedule',
    allowedRoles: ['WAREHOUSE_MANAGER', 'MANUFACTURING_MANAGER'],
  },
  {
    path: '/manufacturing-work-orders',
    allowedRoles: [
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MANUFACTURER',
    ],
  },
  // mfg-program PR-F: work-order detail — same roles as the list (the path
  // matcher is segment-count-exact, so the list entry does not cover it).
  {
    path: '/manufacturing-work-orders/:id',
    allowedRoles: [
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MANUFACTURER',
    ],
  },
  // mfg-program PR-H: stack-label scan station — the roles of
  // POST /manufacturing-work-orders/scan.
  {
    path: '/manufacturing-scan',
    allowedRoles: [
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  // End-of-line Count parts tablet (2026-10-30): the roles of the
  // /manufacturing-work-orders/end-count routes. Mirrored in
  // known-routes.ts, seed-roles-data.ts and the seed migration.
  {
    path: '/manufacturing-count',
    allowedRoles: [
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  // Big-screen board (2026-10-20): read-only view of the open work orders,
  // the dashboard's roles. Mirrored in known-routes.ts, seed-roles-data.ts
  // and the 20261021110000 seed migration.
  {
    path: '/manufacturing-board',
    allowedRoles: [
      'MANUFACTURER',
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
    ],
  },
  {
    path: '/kit-assembly',
    allowedRoles: [
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MANUFACTURER',
    ],
  },
  {
    path: '/kit-putaway',
    allowedRoles: [
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MANUFACTURER',
      'PUTAWAY_OPERATOR',
    ],
  },
  {
    path: '/kits/assembly/:id',
    allowedRoles: [
      'MANUFACTURING_MANAGER',
      'WAREHOUSE_MANAGER',
      'MANUFACTURER',
    ],
  },

  // === OPERATIONS ===
  // Unified locations surface (all 10 LocationType values).
  // Backend: LocationsController + SlottingLayoutController --
  // @WarehouseRoles('WAREHOUSE_MANAGER') on every write.
  { path: '/locations', allowedRoles: ['WAREHOUSE_MANAGER'] },
  { path: '/locations/:id', allowedRoles: ['WAREHOUSE_MANAGER'] },

  // === ADMINISTRATION (admin-only unless noted) ===
  { path: '/users', allowedRoles: 'ADMIN_ONLY' },
  { path: '/reports', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Backend: NotificationsController -- GET: any authenticated, POST create: WAREHOUSE_MANAGER, cleanup: ADMIN
  { path: '/notifications', allowedRoles: 'ANY_AUTHENTICATED' },
  // Backend: ChatController -- GET: any authenticated, channel mgmt: WAREHOUSE_MANAGER
  { path: '/chat', allowedRoles: 'ANY_AUTHENTICATED' },
  // AI assistant — role-open, but additionally owner-gated server-side via the
  // ai_assistant.allowed_emails setting (nav hides for everyone else).
  { path: '/assistant', allowedRoles: 'ANY_AUTHENTICATED' },
  // Scanning a printed serial is a floor activity, not a manager one; the
  // API scopes what each caller can resolve, and a serial from another
  // warehouse 404s exactly like one that does not exist.
  { path: '/label-lookup', allowedRoles: 'ANY_AUTHENTICATED' },
  // Universal scan lookup — same posture as /label-lookup, and for the same
  // reason: the page is open, `GET /search/scan` scopes what each caller can
  // actually resolve, and a code from a warehouse they hold no role at comes
  // back absent, indistinguishable from one that does not exist.
  { path: '/scan', allowedRoles: 'ANY_AUTHENTICATED' },
  // Every authenticated user has their own profile page
  { path: '/profile', allowedRoles: 'ANY_AUTHENTICATED' },
  // Per-device settings (no-scan override, etc.) — reachable by every user;
  // the sensitive scan-flow toggle stays PIN-gated in its own dialog.
  { path: '/device-settings', allowedRoles: 'ANY_AUTHENTICATED' },
  // Every authenticated user manages their own alert opt-in/out preferences.
  { path: '/settings/alerts', allowedRoles: 'ANY_AUTHENTICATED' },
  { path: '/settings/roles', allowedRoles: 'ADMIN_ONLY' },
  { path: '/settings/roles/:id', allowedRoles: 'ADMIN_ONLY' },
  { path: '/settings/ip-allowlist', allowedRoles: 'ADMIN_ONLY' },
  { path: '/settings/allocation-strategy', allowedRoles: 'ADMIN_ONLY' },
  { path: '/settings/dashboard-columns', allowedRoles: 'ADMIN_ONLY' },
  { path: '/settings/api-keys', allowedRoles: 'ADMIN_ONLY' },
  { path: '/settings/printers', allowedRoles: ['WAREHOUSE_MANAGER'] },
  { path: '/settings/label-templates', allowedRoles: ['WAREHOUSE_MANAGER'] },
  {
    path: '/settings/label-templates/:id',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  { path: '/sync-dashboard', allowedRoles: 'ADMIN_ONLY' },
  { path: '/sync-conflicts', allowedRoles: 'ADMIN_ONLY' },
  { path: '/wrap-photos', allowedRoles: 'ADMIN_ONLY' },
  // Action feed: visible to WAREHOUSE_MANAGER + ADMIN (AUDITOR via the isAuditor
  // bypass). The verbose/dev experience is admin-only and the API forces non-
  // admins to the USER tier. Keep in lockstep with apps/api seed-roles-data.ts
  // + roles/known-routes.ts.
  { path: '/actions', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Worker-facing "My stats" — each person's OWN numbers, never anyone else's.
  // Roles come from SELF_PRODUCTIVITY_REGISTRY
  // (apps/api/src/modules/action-log/self-productivity-registry.ts); adding a
  // role there means adding it in all FIVE mirrors: this file,
  // apps/api/src/modules/roles/known-routes.ts, prisma/seed-roles-data.ts and
  // a seed migration (action='access', NOT 'execute'), and
  // pages/settings/roles/permission-groups.ts (its test fails on an unlisted
  // route, and an unlisted one lands under "Other" in the role matrix).
  { path: '/my-stats', allowedRoles: ['PICKER', 'WRAPPER', 'RECOUNTER'] },
  // TEMPORARY FEATURE — external-fulfillment reconcile (admin-only). Remove
  // this route entry once external fulfillment stops. Keep in lockstep with
  // apps/api/prisma/seed-roles-data.ts and roles/known-routes.ts.
  { path: '/external-fulfillment-reconcile', allowedRoles: 'ADMIN_ONLY' },
  // ShipHawk Sync Reconciliation report — admin-only. Backend gates the
  // /reports/shiphawk-sync/* endpoints with @Roles('ADMIN').
  { path: '/reports/shiphawk-sync', allowedRoles: 'ADMIN_ONLY' },

  // === SETTINGS (consolidated under /settings/* — PR 2+3 lockstep migration) ===
  // Parent route: visible to any user with at least one permitted child.
  // canAccessRoute early-returns true for isAdmin/isAuditor
  // so 'ADMIN' is intentionally absent from all allowedRoles arrays here.
  { path: '/settings', allowedRoles: ['WAREHOUSE_MANAGER', 'PICKING_MANAGER'] },
  // /settings/configuration: mirrors old /settings gate (was ADMIN_ONLY; now
  // WAREHOUSE_MANAGER to allow manager-tier config access via nested route).
  { path: '/settings/configuration', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Mirrors old /warehouse-rules gate
  { path: '/settings/warehouse', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Mirrors old /allocation-priority-rules gate
  {
    path: '/settings/priority-rules',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  // Mirrors old /allocation-settings gate
  { path: '/settings/allocation', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Mirrors old /putaway-rules gate
  { path: '/settings/putaway', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Mirrors old /pick-split-rules gate
  { path: '/settings/pick-split', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Channel rules (UnifiedRulesController, CHANNEL)
  { path: '/settings/channel', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Bin reservation rules (UnifiedRulesController, BIN_RESERVATION)
  { path: '/settings/bin-reservation', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Bin priority rules (UnifiedRulesController, BIN_PRIORITY)
  { path: '/settings/bin-priority', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Pick path sort rules (UnifiedRulesController, PICK_PATH_SORT)
  { path: '/settings/pick-path', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Mirrors old /so-routing-rules gate
  { path: '/settings/so-routing', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Mirrors old /auto-tag-rules gate
  { path: '/settings/auto-tag', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // SKU replacement rules (SkuReplacementRulesController, SKU_REPLACEMENT)
  {
    path: '/settings/sku-replacement-rules',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  // Item type overrides (ItemTypeOverridesController) — persistent stock-ness flip
  {
    path: '/settings/item-type-overrides',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  // Layout change requests (LayoutChangeRequestsController) — review/approve
  // Google Sheets layout submissions. Mirrors the backend
  // @WarehouseRoles('WAREHOUSE_MANAGER') guard on the review routes.
  {
    path: '/settings/layout-change-requests',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  // Backend: UnifiedRulesController (CLAIM_ELIGIBILITY) — claim-next pull
  // queue restriction admin page. Mirrors old /assignment-rules gate.
  { path: '/settings/assignment', allowedRoles: ['WAREHOUSE_MANAGER'] },
  { path: '/settings/tags', allowedRoles: ['WAREHOUSE_MANAGER'] },
  // Backend: OrderHoldRuleController/HolidayCalendarController/OrderHoldController
  // — managers can view/release; rule + calendar mutations are
  // WAREHOUSE_MANAGER-gated at the controller level. Reuses the generic
  // rules:create/edit/delete actions.
  {
    path: '/settings/order-holds',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER'],
  },

  // === CATCH-ALL ===
  // The 404 page (*) does not need an entry -- RoleProtectedRoute falls through to render it
];

// ============================================
// ACTION-LEVEL PERMISSIONS
// ============================================

export interface ActionPermission {
  action: string;
  description: string;
  allowedRoles: WarehouseRole[] | 'ADMIN_ONLY';
}

/**
 * The replacement endpoints' gate: the literal WAREHOUSE_MANAGER role at the
 * warehouse named in the request (`@WarehouseRoles('WAREHOUSE_MANAGER')` on
 * POST /replacement-orders and its lookup-order / resolve-original / options
 * reads — no other role implies it). Not the `so:create-replacement` action
 * below, which an admin may also grant to another role: that shows the menu
 * item, the API still refuses until this role is held there. Checked with
 * `usePermissions().hasRoleAt` / `warehousesWithRole`.
 */
export const REPLACEMENT_GATE_ROLE: WarehouseRole = 'WAREHOUSE_MANAGER';

/**
 * Replacements default to North Carolina: the Front link lands there when the
 * rep manages it, and the API stamps the Activity entry of an order that
 * never reached the WMS there.
 */
export const REPLACEMENT_DEFAULT_WAREHOUSE_CODE = 'NC';

/**
 * The warehouse the Front replacement link should land in, out of
 * `candidates` (ids where the user may create one): North Carolina when it is
 * among them, else the first. `codes` are the warehouse store's two lists —
 * either may still be empty on the fresh tab the link opens.
 */
export function pickReplacementWarehouse(
  candidates: string[],
  codes: {
    accessibleWarehouses: ReadonlyArray<{
      warehouseId: string;
      warehouseCode: string;
    }>;
    warehouses: ReadonlyArray<{ id: string; code: string }>;
  },
): string | undefined {
  const isDefault = (id: string) =>
    (codes.accessibleWarehouses.find((w) => w.warehouseId === id)
      ?.warehouseCode ?? codes.warehouses.find((w) => w.id === id)?.code) ===
    REPLACEMENT_DEFAULT_WAREHOUSE_CODE;
  return candidates.find(isDefault) ?? candidates[0];
}

export const ACTION_PERMISSIONS: ActionPermission[] = [
  // Purchase Order actions
  {
    action: 'po:approve-receipt',
    description: 'Approve a PO receipt',
    allowedRoles: ['RECEIVING_MANAGER'],
  },
  {
    action: 'po:repush-receipt',
    description: 'Re-push a receipt to Acumatica',
    allowedRoles: ['RECEIVING_MANAGER'],
  },
  {
    action: 'kit:repush-assembly',
    description: 'Re-push a kit assembly to Acumatica',
    allowedRoles: ['MANUFACTURING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  {
    action: 'kit:edit-assembly',
    description: "Edit a completed kit assembly's SKU and/or quantity",
    allowedRoles: ['MANUFACTURING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  {
    action: 'po:create',
    description: 'Create a new PO',
    allowedRoles: ['RECEIVING_MANAGER'],
  },
  {
    action: 'po:receive',
    description: 'Receive against a PO',
    allowedRoles: ['RECEIVING_MANAGER', 'RECEIVER'],
  },

  // Pick Order actions
  {
    action: 'pick:create',
    description: 'Create a pick order',
    allowedRoles: ['PICKING_MANAGER'],
  },
  {
    action: 'pick:assign',
    description: 'Assign a picker',
    allowedRoles: ['PICKING_MANAGER'],
  },
  {
    action: 'pick:execute',
    description: 'Execute a pick',
    allowedRoles: ['PICKING_MANAGER', 'PICKER'],
  },
  {
    action: 'pick:cancel',
    description: 'Cancel a pick order',
    allowedRoles: ['PICKING_MANAGER'],
  },
  {
    // Mirrors POST /picking/short-picks/:id/resolve (and the order-level
    // resolve-all / escalate-all), which allow RECOUNTER.
    action: 'pick:resolve-short',
    description: 'Resolve a short pick',
    allowedRoles: ['PICKING_MANAGER', 'RECOUNTER'],
  },
  {
    // Short-fill flow (plan §3.6 / §3.9, M9): with the warehouse's flow ON a
    // terminal resolution of a fillable short is SHIP SHORT — a manager
    // decision. Mirrors `assertMayShipShort` (PICKING_MANAGER /
    // WAREHOUSE_MANAGER / admin) so the card never offers a 403.
    action: 'pick:ship-short',
    description: 'Ship an order short (fill flow)',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  {
    // Mirrors GET /picking/short-picks/global-counts, which backs the Sales
    // Orders Short tab's KPI strip. Keep in step with that route's
    // @WarehouseRoles — the strip is hidden for everyone else so it never 403s.
    action: 'pick:view-short-kpis',
    description: 'View the short-pick KPI strip',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER'],
  },

  // Sales Order actions
  {
    action: 'so:transition-status',
    description: 'Transition SO status',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  {
    action: 'so:bulk-action',
    description: 'Bulk SO operations',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  {
    action: 'so:override-hold',
    description: 'Override SO hold',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  {
    // Gates the "Create Replacement Order" menu item on a sales order. The
    // API enforces roles on its own (REPLACEMENT_GATE_ROLE above): granting
    // this action to another role shows the item but the API still refuses
    // until that role is added there too.
    action: 'so:create-replacement',
    description:
      'Create a CS replacement order (Acumatica SR order + Front note)',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },

  // Allocation Queue actions
  // WAREHOUSE_MANAGER is included because they manage all warehouse operations,
  // including queue priority. PICKING_MANAGER also has these actions because
  // they own pick execution. ROLE_HIERARCHY does not auto-expand
  // PICKING_MANAGER inside WAREHOUSE_MANAGER, so both must be listed explicitly.
  {
    action: 'queue:reorder',
    description: 'Reorder allocation queue',
    allowedRoles: ['WAREHOUSE_MANAGER', 'PICKING_MANAGER'],
  },
  {
    action: 'queue:recalculate',
    description: 'Force queue recalculation',
    allowedRoles: ['WAREHOUSE_MANAGER', 'PICKING_MANAGER'],
  },
  {
    action: 'queue:override-priority',
    description: 'Override queue priority',
    allowedRoles: ['WAREHOUSE_MANAGER', 'PICKING_MANAGER'],
  },

  // Work Order actions
  {
    action: 'wo:create',
    description: 'Create work order',
    allowedRoles: ['MANUFACTURING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  {
    action: 'wo:approve',
    description: 'Approve work order',
    allowedRoles: ['MANUFACTURING_MANAGER'],
  },
  {
    action: 'wo:execute',
    description: 'Execute work order',
    allowedRoles: ['MANUFACTURING_MANAGER', 'MANUFACTURER'],
  },
  {
    action: 'wo:cancel',
    description: 'Cancel work order',
    allowedRoles: ['MANUFACTURING_MANAGER'],
  },

  // Inventory actions
  {
    action: 'inv:adjust',
    description: 'Create inventory adjustment',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  {
    action: 'inv:approve-adjustment',
    description: 'Approve inventory adjustment',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  {
    action: 'inv:transfer',
    description: 'Create transfer',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  {
    // Bin-to-bin RELOCATION moves (single-item Move dialog + Bulk Move).
    // Deliberately broader than inv:adjust — inventory managers can move
    // stock but not write it in/out of existence. Cross-WAREHOUSE moves are
    // additionally gated by canCrossWarehouseMove (exact-role, both ends).
    action: 'inv:move',
    description: 'Move inventory between bins',
    allowedRoles: ['WAREHOUSE_MANAGER', 'INVENTORY_MANAGER'],
  },

  // Recount actions
  {
    action: 'recount:create',
    description: 'Create recount',
    allowedRoles: ['PICKING_MANAGER'],
  },
  {
    action: 'recount:execute',
    description: 'Execute recount scan',
    // PICKER included: the RecountController allows PICKER on the scan/
    // start/complete endpoints, and pickers run recounts in this warehouse.
    allowedRoles: ['PICKING_MANAGER', 'PICKER', 'RECOUNTER'],
  },
  {
    // Manager reassign of an active recount (PATCH /recount/:id/assignee).
    // Mirrors the backend @WarehouseRoles guard.
    action: 'recount:reassign',
    description: 'Reassign recount to a new operator',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER'],
  },

  // Wrap actions
  {
    // Manager push-assign a wrap (POST /wrapping). Also gates the SO-list
    // "Assign wrap" action and the Wrapping management tab visibility.
    action: 'wrap:assign',
    description: 'Assign wrap to a wrapper',
    allowedRoles: ['PICKING_MANAGER', 'WAREHOUSE_MANAGER'],
  },
  {
    // Add a pallet photo to an ALREADY-COMPLETED order
    // (POST /wrapping/sales-orders/:id/photos). Roles mirror that route's
    // @WarehouseRoles guard exactly — the FE gate is UX, the guard is
    // enforcement, and the two must not drift.
    action: 'wrap:photo-post-completion',
    description: 'Add pallet photos after an order is completed',
    allowedRoles: ['PICKING_MANAGER', 'WRAPPER'],
  },

  // Count Batches actions (cycle counting v2)
  {
    action: 'count:execute',
    description: 'Count bins (counter page)',
    allowedRoles: ['COUNTER'],
  },
  {
    action: 'count:manage',
    description: 'Create and manage count batches and stages',
    allowedRoles: ['COUNT_MANAGER'],
  },
  {
    action: 'count:approve',
    description: 'Approve or reject count variances',
    allowedRoles: ['COUNT_MANAGER'],
  },
  {
    action: 'count:erp-push',
    description: 'Push approved count adjustments to Acumatica',
    allowedRoles: ['COUNT_MANAGER'],
  },

  // Rules actions
  {
    action: 'rules:create',
    description: 'Create rules',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  {
    action: 'rules:edit',
    description: 'Edit rules',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  {
    action: 'rules:delete',
    description: 'Delete rules',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },

  // User Management
  {
    action: 'users:create',
    description: 'Create users',
    allowedRoles: 'ADMIN_ONLY',
  },
  {
    action: 'users:edit',
    description: 'Edit users',
    allowedRoles: 'ADMIN_ONLY',
  },
  {
    action: 'users:deactivate',
    description: 'Deactivate users',
    allowedRoles: 'ADMIN_ONLY',
  },
  {
    action: 'users:assign-warehouse',
    description: 'Assign user to warehouse',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },

  // Settings & Config
  {
    action: 'settings:edit',
    description: 'Edit system settings',
    allowedRoles: 'ADMIN_ONLY',
  },
  {
    action: 'sync:trigger',
    description: 'Trigger ERP sync',
    allowedRoles: 'ADMIN_ONLY',
  },
  {
    action: 'apikeys:manage',
    description: 'Manage API keys',
    allowedRoles: 'ADMIN_ONLY',
  },

  // Putaway actions
  {
    action: 'putaway:execute',
    description: 'Execute putaway task',
    allowedRoles: ['WAREHOUSE_MANAGER', 'PUTAWAY_OPERATOR'],
  },
  {
    action: 'putaway:override',
    description: 'Override putaway suggestion',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },

  // Shipping actions
  {
    action: 'ship:load',
    description: 'Load shipment',
    allowedRoles: ['WAREHOUSE_MANAGER', 'LOADER'],
  },
  {
    action: 'ship:manage',
    description: 'Manage shipments',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },

  // Notification actions (route is ANY_AUTHENTICATED but create requires WAREHOUSE_MANAGER)
  {
    action: 'notification:create',
    description: 'Create notification',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  {
    action: 'notification:cleanup',
    description: 'Cleanup expired notifications',
    allowedRoles: 'ADMIN_ONLY',
  },

  // Chat actions (route is ANY_AUTHENTICATED but channel mgmt requires WAREHOUSE_MANAGER)
  {
    action: 'chat:create-channel',
    description: 'Create chat channel',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },
  {
    action: 'chat:manage-members',
    description: 'Add/remove channel members',
    allowedRoles: ['WAREHOUSE_MANAGER'],
  },

  // Kitchens (PR-A2, end to end §3.2 / D11): add a kitchen colour — name +
  // 3-letter code only (POST /kitchen-catalog/colors). Joe's team + the
  // catalog editors; prices / decor stay catalog-editor fields.
  {
    action: 'kitchen-colors:add',
    description: 'Add a kitchen colour (name + 3-letter code)',
    allowedRoles: ['MULTI_FAMILY', 'KITCHEN_ENGINEER', 'WAREHOUSE_MANAGER'],
  },
];

// ============================================
// PERMISSION CHECK HELPERS
// ============================================

/**
 * Expand a list of roles using the hierarchy.
 * e.g., ['PICKING_MANAGER'] -> ['PICKING_MANAGER', 'PICKER', 'RECOUNTER']
 *
 * This expands DOWNWARD: if you HAVE a manager role, you also HAVE the operator roles.
 * It does NOT go upward: having PICKER does not give you PICKING_MANAGER.
 */
export function expandRoles(roles: WarehouseRole[]): Set<WarehouseRole> {
  const expanded = new Set<WarehouseRole>(roles);
  for (const role of roles) {
    const implied = ROLE_HIERARCHY[role];
    if (implied) {
      implied.forEach((r) => expanded.add(r));
    }
  }
  return expanded;
}

/**
 * Check if a user with the given roles can access a route.
 *
 * NOTE on matchPath: This uses exact segment matching.
 * - Trailing slashes: '/picking/' will NOT match '/picking'. Normalize paths before calling.
 * - Query params: Strip query string before calling (match on pathname only).
 * - Hash fragments: Strip hash before calling.
 *
 * @param routePermissions Optional override permission set (e.g. API-fetched data).
 *   Falls back to the bundled ROUTE_PERMISSIONS array when not provided.
 */
export function canAccessRoute(
  userRoles: WarehouseRole[],
  isAdmin: boolean,
  isAuditor: boolean,
  routePath: string,
  routePermissions: RoutePermission[] = ROUTE_PERMISSIONS,
): boolean {
  if (isAdmin) return true;
  // Auditors can navigate to any route, including ADMIN_ONLY routes
  // (/users, /settings, /sync-dashboard, /actions). This is intentional:
  // auditors need read-only visibility into everything for compliance
  // review. Write-blocking is enforced at the action level via
  // canPerformAction(), which always returns false for isAuditor.
  if (isAuditor) return true;

  // Normalize: strip trailing slash (except root), query params, hash
  const normalizedPath = normalizePath(routePath);

  // A legacy redirect is not a page and has nothing to authorize — let it
  // through and let the target it forwards to run the real check.
  if (isLegacyRedirect(normalizedPath)) return true;

  const permission = routePermissions.find((rp) =>
    matchPath(rp.path, normalizedPath),
  );
  if (!permission) return false; // unknown route = deny

  if (permission.allowedRoles === 'ANY_AUTHENTICATED') return true;
  if (permission.allowedRoles === 'ADMIN_ONLY') return false;

  const expandedUserRoles = expandRoles(userRoles);
  return permission.allowedRoles.some((role) => expandedUserRoles.has(role));
}

/**
 * Check if a user can perform a specific action.
 *
 * @param actionPermissions Optional override permission set (e.g. API-fetched data).
 *   Falls back to the bundled ACTION_PERMISSIONS array when not provided.
 */
export function canPerformAction(
  userRoles: WarehouseRole[],
  isAdmin: boolean,
  isAuditor: boolean,
  action: string,
  actionPermissions: ActionPermission[] = ACTION_PERMISSIONS,
): boolean {
  if (isAdmin) return true;
  if (isAuditor) return false; // auditors CANNOT perform write actions

  const permission = actionPermissions.find((ap) => ap.action === action);
  if (!permission) return false; // unknown action = deny

  if (permission.allowedRoles === 'ADMIN_ONLY') return false;

  const expandedUserRoles = expandRoles(userRoles);
  return permission.allowedRoles.some((role) => expandedUserRoles.has(role));
}

/**
 * Cross-warehouse inventory-move authorization (mirrors the backend's
 * `canCrossWarehouseMove` in apps/api/src/modules/auth/utils/).
 *
 * Moving stock BETWEEN warehouses requires global admin, or the EXACT
 * `INVENTORY_MANAGER` role — literally assigned, NOT inherited via
 * ROLE_HIERARCHY — at EVERY involved warehouse. This intentionally inverts
 * the usual hierarchy rule: a WAREHOUSE_MANAGER cannot move cross-warehouse
 * unless INVENTORY_MANAGER is also explicitly assigned at both ends.
 */
export function canCrossWarehouseMove(
  warehouseRolesByWarehouse: Record<string, WarehouseRole[]> | undefined,
  isAdmin: boolean,
  warehouseIds: string[],
): boolean {
  if (isAdmin) return true;
  const roles = warehouseRolesByWarehouse ?? {};
  return warehouseIds.every((wh) =>
    (roles[wh] ?? []).includes('INVENTORY_MANAGER'),
  );
}

/**
 * Normalize a path for matching:
 * - Strip trailing slash (except root '/')
 * - Strip query string
 * - Strip hash fragment
 */
function normalizePath(path: string): string {
  // Strip query params and hash
  let clean = path.split('?')[0].split('#')[0];
  // Strip trailing slash (but keep root '/')
  if (clean.length > 1 && clean.endsWith('/')) {
    clean = clean.slice(0, -1);
  }
  return clean;
}

/**
 * Simple path matcher that handles :param segments.
 * '/picking/:id' matches '/picking/abc-123'
 */
function matchPath(pattern: string, actual: string): boolean {
  const patternParts = pattern.split('/');
  const actualParts = actual.split('/');
  if (patternParts.length !== actualParts.length) return false;
  return patternParts.every(
    (part, i) => part.startsWith(':') || part === actualParts[i],
  );
}
